import React, { useEffect, useRef, useState } from "react"
import { IoCloseSharp } from "react-icons/io5"
import { FiLink, FiChevronLeft, FiChevronRight, FiMaximize } from "react-icons/fi"
import type { Project } from "../lib/types"
import placeholder from "../assets/plchldr.jpg"
import SkeletonImage from "./SkeletonImage"

interface ModalProps {
  visible: boolean
  modalData: Project | null | undefined
  handleClose: () => void
  onNext?: () => void
  onPrev?: () => void
}

const Modal: React.FC<ModalProps> = ({
  visible,
  modalData,
  handleClose,
  onNext,
  onPrev,
}) => {
  const project: Project = modalData || ({} as Project)
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [copied, setCopied] = useState(false)
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null)

  // Fallback data
  const displayRole = project.role || "Full Stack Developer"
  const displayDescription = project.description || "A comprehensive project showcasing advanced web development techniques and modern architecture. This project focuses on delivering a seamless user experience with high performance and scalability."
  const displaySkills = project.skills && project.skills.length > 0 ? project.skills : ["React", "TypeScript", "Tailwind CSS", "Node.js", "Laravel"]
  const displayDate = project.publishedAt || "Jan 20, 2026"
  const displayProjectType = project.projectType || "Full Stack"
  const displayAssets = project.assets && project.assets.length > 0 
    ? project.assets 
    : [{ type: "image", url: project.cover || placeholder }]

  useEffect(() => {
    if (!visible) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
      if (e.key === "ArrowRight" && onNext) onNext()
      if (e.key === "ArrowLeft" && onPrev) onPrev()
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [visible, handleClose, onNext, onPrev])

  const copyLink = async () => {
    const url = `${window.location.origin}/projects?slug=${project.slug}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      // ignore
    }
  }

  const getEmbedUrl = (url: string) => {
    if (!url) return null
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const id = url.split("v=")[1] || url.split("/").pop()
      return `https://www.youtube.com/embed/${id}`
    }
    if (url.includes("vimeo.com")) {
      const id = url.split("/").pop()
      return `https://player.vimeo.com/video/${id}`
    }
    return null
  }

  if (!visible) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-start lg:items-center justify-center p-5 pt-12 sm:p-8 lg:p-12 overflow-y-auto">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity"
        onClick={handleClose}
      />

      {/* Navigation Arrows (Outside Modal) */}
      <button
        onClick={onPrev}
        className="hidden lg:flex fixed left-4 xl:left-12 z-[110] p-4 rounded-full bg-white/10 hover:bg-white/20 text-[#f5f1f9] transition-all transform hover:scale-110 active:scale-95"
        aria-label="Previous project"
      >
        <FiChevronLeft size={40} />
      </button>
      <button
        onClick={onNext}
        className="hidden lg:flex fixed right-4 xl:right-12 z-[110] p-4 rounded-full bg-white/10 hover:bg-white/20 text-[#f5f1f9] transition-all transform hover:scale-110 active:scale-95"
        aria-label="Next project"
      >
        <FiChevronRight size={40} />
      </button>

      {/* Modal Content */}
      <div
        ref={modalRef}
        className="relative w-full max-w-7xl h-[85vh] lg:h-[90vh] bg-[#1a171b] text-[#f5f1f9] rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-300 border border-[#221e24]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 lg:px-10 border-b border-[#221e24] bg-[#1a171b] z-10">
          <div className="flex flex-col min-w-0">
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-[#f5f1f9] pr-4 tracking-tight truncate">
              {project.name || "Project Details"}
            </h1>
            <span className="text-[#6b69f2] font-bold text-[10px] md:text-sm uppercase tracking-widest mt-0.5">
              {displayProjectType} Project
            </span>
          </div>
          <div className="flex items-center gap-3 md:gap-8 shrink-0">
            <button
              onClick={copyLink}
              className="flex items-center gap-2 text-[#6b69f2] hover:text-[#5956f0] font-bold transition-colors text-lg md:text-xl"
            >
              <span className="hidden sm:inline">{copied ? "Copied!" : "Copy link"}</span>
              <FiLink size={20} className="md:w-6 md:h-6" />
            </button>
            <button
              onClick={handleClose}
              className="p-1.5 md:p-2 hover:bg-[#221e24] rounded-full text-[#79747b] hover:text-[#f5f1f9] transition-colors"
              aria-label="Close"
            >
              <IoCloseSharp size={28} className="md:w-9 md:h-9" />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 scrl-br">
          <div className="flex flex-col lg:flex-row">
            {/* Left Column: Details (Sticky on Desktop) */}
            <div className="lg:w-1/3 p-5 md:p-10 lg:p-12 space-y-8 md:space-y-10 flex flex-col lg:sticky lg:top-0 h-fit">
              <div>
                <h3 className="text-[#79747b] font-semibold mb-2 text-sm uppercase tracking-widest">My role</h3>
                <p className="text-2xl text-[#f5f1f9] font-extrabold">{displayRole}</p>
              </div>

              <div>
                <h3 className="text-[#79747b] font-semibold mb-3 text-sm uppercase tracking-widest">Project description</h3>
                <div className="text-[#79747b] leading-relaxed whitespace-pre-wrap text-lg font-medium">
                  {displayDescription}
                </div>
              </div>

              <div>
                <h3 className="text-[#79747b] font-semibold mb-4 text-sm uppercase tracking-widest">Skills and deliverables</h3>
                <div className="flex flex-wrap gap-3">
                  {displaySkills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-5 py-2 bg-[#221e24] text-[#79747b] rounded-full text-sm font-bold border border-[#39353B]"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-[#221e24]">
                <p className="text-lg text-[#79747b] font-medium">
                  Published on <span className="text-[#f5f1f9] font-bold">{displayDate}</span>
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center py-4 bg-[#6b69f2] text-[#f5f1f9] rounded-xl font-bold text-lg hover:bg-[#5956f0] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    GitHub Repo
                  </a>
                )}
                {project.demoLink && (
                  <a
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center py-4 border-2 border-[#221e24] text-[#f5f1f9] rounded-xl font-bold text-lg hover:bg-[#221e24] transition-all shadow-sm hover:shadow-md transform hover:-translate-y-1"
                  >
                    Live Demo
                  </a>
                )}
              </div>

              {/* <div className="pt-6">
                <button className="text-[#79747b] hover:text-[#f5f1f9] text-sm underline font-medium transition-colors">
                  Report an issue
                </button>
              </div> */}
            </div>

            {/* Right Column: Media (Scrollable vertical list) */}
            <div className="lg:w-2/3">
              {displayAssets.map((asset, index) => (
                <div key={index} className="w-full relative group mb-[10px]">
                  {asset.type === "video" ? (
                    <iframe
                      src={getEmbedUrl(asset.url) || ""}
                      className="w-full aspect-video"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={`${project.name} video ${index}`}
                    ></iframe>
                  ) : (
                    <>
                      <div className="relative w-full aspect-video">
                        <SkeletonImage
                          src={asset.url || placeholder}
                          alt={`${project.name} asset ${index}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => setFullScreenImage(asset.url)}
                        className="absolute top-4 right-4 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        aria-label="View full screen"
                      >
                        <FiMaximize size={24} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Full-screen Image Overlay */}
        {fullScreenImage && (
          <div 
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-xl animate-in fade-in duration-300"
            onClick={() => setFullScreenImage(null)}
          >
            <button
              onClick={() => setFullScreenImage(null)}
              className="absolute top-6 right-6 p-2 hover:bg-white/10 rounded-full text-white transition-colors"
              aria-label="Close full screen"
            >
              <IoCloseSharp size={40} />
            </button>
            <img
              src={fullScreenImage}
              alt="Full screen view"
              className="max-w-[95vw] max-h-[95vh] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Mobile Navigation */}
        <div className="lg:hidden flex items-center justify-between p-4 border-t border-[#221e24] bg-[#1a171b]">
          <button
            onClick={onPrev}
            className="flex items-center gap-2 text-[#f5f1f9] font-bold text-base px-4 py-2 hover:bg-[#221e24] active:bg-[#2a262c] rounded-lg transition-colors"
          >
            <FiChevronLeft size={20} />
            Previous
          </button>
          <button
            onClick={onNext}
            className="flex items-center gap-2 text-[#f5f1f9] font-bold text-base px-4 py-2 hover:bg-[#221e24] active:bg-[#2a262c] rounded-lg transition-colors"
          >
            Next
            <FiChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Modal
